import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get, post } from "../lib/api";
import { Seo } from "../lib/seo";
import { Section, SectionHead, Button } from "../components/ui";
import { PricingTable, DataTable, FaqAccordion, Yes, No } from "../components/marketing";
import { useMoney } from "../lib/currency";
import { useReveal } from "../lib/useReveal";

const FEATURE_ROWS = [
  ["Voice-note understanding", true, true, true],
  ["Image understanding", true, true, true],
  ["Video and document understanding", true, true, true],
  ["Incoming campaigns", true, true, true],
  ["Outgoing campaigns", true, true, true],
  ["Combined campaigns", false, true, true],
  ["Contact tagging", true, true, true],
  ["Unlimited contacts", true, true, true],
  ["One-click optimise", true, true, true],
  ["Reply to comments", true, true, true],
  ["AI appointment booking", true, true, true],
  ["Automatic follow-ups", false, true, true],
  ["Real-time web search", false, true, true],
  ["Custom Functions", false, true, true],
  ["Webhooks", true, true, true],
  ["API access", true, true, true],
  ["BYOK", "Add-on", true, true],
  ["Advanced Mode", false, true, true],
  ["Priority support and DPA", false, false, true],
  ["SSO and custom retention", false, false, true],
];

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const [selected, setSelected] = useState(null);
  const money = useMoney();
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: () => get("/plans") });
  const { data: packs } = useQuery({ queryKey: ["packs"], queryFn: () => get("/credit-packs") });
  const { data: faqData } = useQuery({ queryKey: ["faqs"], queryFn: () => get("/faqs") });
  useReveal(plans);
  const pricingFaqs = (faqData?.faqs || []).filter((f) => ["Plans & pricing", "Credits, billing & BYOK"].includes(f.category));

  return (
    <>
      <Seo title="Pricing" path="/pricing/" description="BloomCONNECT pricing in Philippine pesos. Growth ₱5,995 and Pro ₱11,995 a month, plus flat-rate credits. Two months free on annual billing."
        jsonLd={{ "@context": "https://schema.org", "@type": "Product", name: "BloomCONNECT", description: "AI sales agent", offers: (plans || []).filter(p => p.php_monthly).map(p => ({ "@type": "Offer", name: p.name, price: p.php_monthly, priceCurrency: "PHP" })) }} />
      <Section>
        <SectionHead label="Pricing" title="Pay for what you use. No per-seat sales tax on growth." intro="Prices in Philippine pesos. USD shown for reference. We accept Credit/Debit cards via PayPal. Conversation estimates run at Max tier (0.25 credits per action) — roughly 4× the capacity of standard rates." />
        <div className="mt-8 flex items-center gap-3">
          <div className="inline-flex rounded-xs border border-line overflow-hidden">
            {["monthly", "annual"].map((b) => (
              <button key={b} onClick={() => setBilling(b)} className={`px-4 py-2 text-sm capitalize ${billing === b ? "bg-ink-950 text-white" : "text-ink-600"}`}>{b}</button>
            ))}
          </div>
          <span className="font-mono text-2xs text-brand-700">Annual = two months free</span>
        </div>
        <div className="mt-10">{plans && <PricingTable plans={plans} billing={billing} onSelect={setSelected} />}</div>
      </Section>
      {selected && <CheckoutModal plan={selected} billing={billing} onClose={() => setSelected(null)} />}

      <Section sunk>
        <SectionHead label="Compare" title="Every feature, by plan." />
        <div className="mt-8 overflow-x-auto">
          <table className="w-full border-collapse">
            <caption className="sr-only">Feature comparison</caption>
            <thead><tr className="border-b border-line">
              <th scope="col" className="py-3 px-3 text-left font-mono text-2xs uppercase tracking-eyebrow text-ink-400">Feature</th>
              {["Growth", "Pro", "Enterprise"].map((h) => <th key={h} scope="col" className="py-3 px-3 text-center font-mono text-2xs uppercase tracking-eyebrow text-ink-600">{h}</th>)}
            </tr></thead>
            <tbody>
              {FEATURE_ROWS.map((r) => (
                <tr key={r[0]} className="border-b border-line">
                  <td className="py-2.5 px-3 text-sm text-ink-800">{r[0]}</td>
                  {r.slice(1).map((v, i) => <td key={i} className="py-2.5 px-3 text-center">{typeof v === "string" ? <span className="font-mono text-2xs uppercase tracking-eyebrow text-brand-700">{v}</span> : v ? <Yes /> : <No />}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section>
        <SectionHead label="Credits" title="Flat-rate credits, metered exactly." />
        <div className="mt-8">
          <DataTable caption="Credit costs" head={["What it is", "Cost"]} rows={[
            ["1 credit", `${money(10)} (≈ $0.16)`],
            ["AI response", "1 credit per thinking cycle"],
            ["AI tool use (booking, web search, Custom Function, campaign/FAQ generation)", "1 credit per call"],
            ["WhatsApp Web connection", "50 credits / month per number"],
            ["WhatsApp Business API message", "0.05 credits (Twilio pass-through, no mark-up)"],
            ["WhatsApp template message", "Meta's published rate, passed through"],
            ["Phone numbers", "Twilio rate card, passed through"],
            ["SMS", "Billed by your own Twilio account"],
          ]} />
        </div>
        <h3 className="mt-12 text-xl text-ink-950">Credit top-up packs</h3>
        <div className="mt-4">
          {packs && <DataTable caption="Top-up packs" head={["Pack", "Price", "Effective"]}
            rows={packs.map((p) => [p.label, money(p.php_price), p.effective])} />}
        </div>
      </Section>

      <Section sunk>
        <div className="grid gap-8 lg:grid-cols-2 items-start">
          <div className="card-hairline p-6">
            <div className="eyebrow mb-3">BYOK · Pro and Enterprise</div>
            <h3 className="text-2xl text-ink-950">Bring your own Anthropic key.</h3>
            <p className="mt-3 text-ink-600">Connect your own Anthropic API key and AI operations stop consuming credits. Channel infrastructure costs still apply.</p>
            <Button to="/tools/anthropic-api-spend-estimator/" variant="secondary" size="md" className="mt-5">Estimate your Anthropic spend</Button>
          </div>
          <div className="card-hairline p-6">
            <div className="eyebrow mb-3">The comparison</div>
            <h3 className="text-2xl text-ink-950">Cheaper than the alternatives.</h3>
            <ul className="mt-4 space-y-2 text-sm text-ink-600">
              <li className="flex justify-between border-b border-line pb-2"><span>A sales hire</span><span className="font-mono text-ink-800">₱25k–₱60k/mo</span></li>
              <li className="flex justify-between border-b border-line pb-2"><span>A virtual assistant</span><span className="font-mono text-ink-800">₱15k–₱35k/mo</span></li>
              <li className="flex justify-between border-b border-line pb-2"><span>A DIY tool stack</span><span className="font-mono text-ink-800">₱8k+/mo + your time</span></li>
              <li className="flex justify-between"><span>BloomCONNECT Growth</span><span className="font-mono text-brand-700">{money(5995)}/mo</span></li>
            </ul>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHead label="FAQ" title="Pricing questions." />
        <div className="mt-8 max-w-3xl">{pricingFaqs.length > 0 && <FaqAccordion items={pricingFaqs} />}</div>
      </Section>
    </>
  );
}

let _ppSdkPromise = null;
function loadPaypalSdk(clientId) {
  if (window.paypal) return Promise.resolve(window.paypal);
  if (_ppSdkPromise) return _ppSdkPromise;
  _ppSdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&currency=PHP`;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => { _ppSdkPromise = null; reject(new Error("PayPal SDK failed to load")); };
    document.body.appendChild(s);
  });
  return _ppSdkPromise;
}

const peso = (v) => "\u20b1" + Number(v || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CheckoutModal({ plan, billing, onClose }) {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, error: "" });
  const [pp, setPp] = useState(null);         // paypal public config
  const [ppState, setPpState] = useState("idle"); // idle | ready | setup | error
  const btnRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    get("/paypal/public-config").then((c) => { if (!cancelled) setPp(c); }).catch(() => setPp({ configured: false }));
    return () => { cancelled = true; };
  }, []);

  const planId = pp?.configured ? (billing === "annual" ? pp.plans_annual?.[plan.slug] : pp.plans?.[plan.slug]) : null;
  const usePaypal = Boolean(planId);
  const subBase = billing === "annual" ? plan.php_annual : plan.php_monthly;
  const subFee = subBase ? Math.round((subBase * (pp?.paypal_fee_pct ?? 0.034) + (pp?.paypal_fee_fixed ?? 15)) * 100) / 100 : 0;
  const subTotal = subBase ? Math.round((subBase + subFee) * 100) / 100 : 0;
  const perLabel = billing === "annual" ? "yr" : "mo";

  useEffect(() => {
    if (!usePaypal || !btnRef.current) return;
    let buttons; let closed = false;
    loadPaypalSdk(pp.client_id).then((paypal) => {
      if (closed || !btnRef.current) return;
      btnRef.current.replaceChildren();
      buttons = paypal.Buttons({
        style: { shape: "pill", layout: "vertical", label: "subscribe" },
        createSubscription: (data, actions) => actions.subscription.create({ plan_id: planId }),
        onApprove: async (data) => {
          setPpState("setup");
          try {
            const r = await post("/checkout/subscription/confirm", { subscription_id: data.subscriptionID });
            if (r?.ok && r.onboarding_token) {
              nav("/welcome", { state: { token: r.onboarding_token, email: r.email, summary: r.summary } });
            }
          } catch (e) { /* stay on the setup screen — webhook will finish provisioning */ }
        },
        onError: () => setPpState("error"),
      });
      if (buttons.isEligible && !buttons.isEligible()) { setPpState("error"); return; }
      return buttons.render(btnRef.current).then(() => { if (!closed) setPpState("ready"); })
        .catch(() => { if (!closed) setPpState("error"); });
    }).catch(() => { if (!closed) setPpState("error"); });
    return () => { closed = true; try { buttons && buttons.close(); } catch (e) { /* noop */ } };
  }, [usePaypal, planId, pp]);

  const start = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      const r = await post("/checkout/subscription", { plan_slug: plan.slug, billing_period: billing, email });
      if (r.checkout_url) { window.location.href = r.checkout_url; return; }
      nav(`/checkout/success/?order_id=${r.order_id}&type=subscription`);
    } catch (err) {
      setState({ loading: false, error: err?.response?.data?.detail || "Could not start checkout." });
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-ink-950/40 grid place-items-center z-50 p-4" onClick={onClose} data-testid="checkout-modal">
      <div className="bg-white rounded-lg border border-line w-[420px] max-w-full p-6" onClick={(e) => e.stopPropagation()}>
        {ppState === "setup" ? (
          <div className="text-center py-4" data-testid="checkout-setup">
            <h2 className="text-2xl text-ink-950">Setting up your account…</h2>
            <p className="text-sm text-ink-600 mt-2">Your PayPal subscription is confirmed. We're provisioning your BloomCONNECT account — you'll get a sign-in email shortly.</p>
            <button onClick={onClose} className="btn-primary btn-md mt-5" data-testid="checkout-setup-close">Done</button>
          </div>
        ) : usePaypal ? (
          <div data-testid="checkout-paypal">
            <div className="eyebrow text-brand-700">Subscribe · PHP · {billing}</div>
            <h2 className="text-2xl text-ink-950 mt-2">{plan.name} plan</h2>
            <p className="text-sm text-ink-600 mt-1">Complete checkout right here with PayPal — no redirect. We accept Credit/Debit cards via PayPal.</p>
            {subBase ? (
              <div className="rounded-lg bg-surface-sunk border border-line p-4 text-sm mt-4" data-testid="checkout-sub-summary">
                <div className="flex justify-between"><span className="text-ink-500">{plan.name} ({billing})</span><span>{peso(subBase)}/{perLabel}</span></div>
                <div className="flex justify-between mt-1"><span className="text-ink-500">PayPal processing fee (3.4% + ₱15)</span><span data-testid="checkout-sub-fee">{peso(subFee)}</span></div>
                <div className="flex justify-between mt-2 pt-2 border-t border-line font-semibold text-ink-950"><span>Billed per {perLabel === "yr" ? "year" : "month"}</span><span data-testid="checkout-sub-total">{peso(subTotal)}/{perLabel}</span></div>
              </div>
            ) : null}
            <div ref={btnRef} className="mt-5 min-h-[52px]" data-testid="paypal-button-container" />
            {ppState === "error" && <p className="text-negative text-xs mt-2">PayPal couldn't load. Please refresh or contact support.</p>}
            <button type="button" onClick={onClose} className="btn-secondary btn-md w-full mt-3">Cancel</button>
          </div>
        ) : (
          <>
            <div className="eyebrow">Talk to us</div>
            <h2 className="text-2xl text-ink-950 mt-2">{plan.name} plan</h2>
            <p className="text-sm text-ink-600 mt-1">Leave your email and our team will set you up right away.</p>
            <form onSubmit={start} className="mt-5">
              <label className="block"><span className="eyebrow block mb-1">Work email</span><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="field" data-testid="checkout-email" /></label>
              {state.error && <p className="text-negative text-xs mt-2" role="alert">{state.error}</p>}
              <div className="flex gap-2 mt-5"><button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">Cancel</button><button type="submit" disabled={state.loading} className="btn-primary btn-md flex-1">{state.loading ? "Starting…" : "Continue"}</button></div>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
